<ion-app [class.ion-palette-dark]="theme.isDark()">
  <ion-menu contentId="main-content" class="app-menu">
    <!-- Brand bar. A toolbar, so Ionic handles the notch / status-bar inset. -->
    <ion-header class="ion-no-border">
      <ion-toolbar class="menu-bar">
        <div class="brand" slot="start">
          <span class="brand-mark">
            <img src="assets/img/appicon.svg" alt="" aria-hidden="true" />
          </span>
          <span class="brand-text">
            <span class="brand-name">{{ siteSettings.brandName() }}</span>
            <span class="brand-tag">{{ 'menu.brandTag' | translate }}</span>
          </span>
        </div>
        <ion-buttons slot="end">
          <ion-menu-toggle>
            <ion-button
              class="menu-close"
              [attr.aria-label]="'menu.close' | translate"
            >
              <ion-icon slot="icon-only" [icon]="menuIcons.close"></ion-icon>
            </ion-button>
          </ion-menu-toggle>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <!-- Identity: who is signed in, right under the brand bar. "ADMIN" for
           an admin account, the account's name otherwise. Nothing shows here
           while signed out — the Account section below is the way in. -->
      @if (loggedIn()) {
        <div class="menu-identity" [class.is-admin]="isAdmin()">
          <span class="menu-identity-icon" aria-hidden="true">
            <ion-icon
              [icon]="isAdmin() ? menuIcons.dashboard : menuIcons.account"
            ></ion-icon>
          </span>
          <span class="menu-identity-name">{{
            isAdmin() ? ('menu.adminBadge' | translate) : username()
          }}</span>
        </div>
      }

      <!-- Product -->
      <nav class="menu-group" [attr.aria-label]="'menu.product' | translate">
        <h2 class="menu-heading">{{ 'menu.product' | translate }}</h2>
        <ion-list lines="none">
          @for (p of productPages; track p.url) {
            <ion-menu-toggle autoHide="false">
              <ion-item
                class="nav-item"
                [style.--tone]="p.tone"
                [routerLink]="p.url"
                routerLinkActive="selected"
                [routerLinkActiveOptions]="{ exact: p.url === '/' }"
                routerDirection="root"
                detail="false"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon [icon]="p.icon" aria-hidden="true"></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ 'nav.' + p.key | translate }}</h3>
                  <p>{{ 'menu.' + p.key + 'Desc' | translate }}</p>
                </ion-label>
              </ion-item>
            </ion-menu-toggle>
          }
        </ion-list>
      </nav>

      <!-- Admin: one expandable group holding every admin page — only for an
           admin-role account (AuthService.isAdmin). Opens by itself when you
           navigate into /admin/*, and can be folded away by hand. -->
      @if (isAdmin()) {
        <nav class="menu-group" [attr.aria-label]="'menu.admin' | translate">
          <h2 class="menu-heading">{{ 'menu.admin' | translate }}</h2>
          <ion-list lines="none">
            <ion-item
              class="nav-item admin-group-toggle"
              button="true"
              [class.group-active]="adminRouteActive()"
              [style.--tone]="'#f59e0b'"
              [attr.aria-expanded]="adminOpen()"
              aria-controls="menu-admin"
              detail="false"
              (click)="adminOpen.set(!adminOpen())"
            >
              <span slot="start" class="nav-ico">
                <ion-icon
                  [icon]="menuIcons.dashboard"
                  aria-hidden="true"
                ></ion-icon>
              </span>
              <ion-label>
                <h3>{{ 'menu.admin' | translate }}</h3>
              </ion-label>
              <ion-icon
                slot="end"
                class="admin-group-chevron"
                [icon]="menuIcons.chevron"
                aria-hidden="true"
              ></ion-icon>
            </ion-item>
          </ion-list>
          <div
            id="menu-admin"
            class="collapsible admin-group-panel"
            [class.open]="adminOpen()"
            [inert]="!adminOpen()"
          >
            <ion-list lines="none" class="admin-group-list">
              @for (a of adminPages; track a.url) {
                <ion-menu-toggle autoHide="false">
                  <ion-item
                    class="nav-item admin-child"
                    [style.--tone]="a.tone"
                    [routerLink]="a.url"
                    routerLinkActive="selected"
                    [routerLinkActiveOptions]="{ exact: true }"
                    routerDirection="root"
                    detail="false"
                  >
                    <span slot="start" class="nav-ico">
                      <ion-icon [icon]="a.icon" aria-hidden="true"></ion-icon>
                    </span>
                    <ion-label>
                      <h3>{{ 'menu.' + a.key | translate }}</h3>
                    </ion-label>
                  </ion-item>
                </ion-menu-toggle>
              }
            </ion-list>
          </div>
        </nav>
      }

      <!-- Account: the way in when signed out, your account and the way out
           when signed in. Rows like the product links, with their own tones. -->
      <nav class="menu-group" [attr.aria-label]="'menu.account' | translate">
        <h2 class="menu-heading">{{ 'menu.account' | translate }}</h2>
        <ion-list lines="none">
          @if (loggedIn()) {
            <!-- The account's name row is for regular accounts; an admin is
                 already identified by the ADMIN badge at the top. -->
            @if (!isAdmin()) {
              <ion-item
                class="nav-item"
                [style.--tone]="'#6b7280'"
                detail="false"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon
                    [icon]="menuIcons.account"
                    aria-hidden="true"
                  ></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ username() || ('menu.account' | translate) }}</h3>
                </ion-label>
              </ion-item>
            }
            <ion-menu-toggle autoHide="false">
              <ion-item
                class="nav-item"
                [style.--tone]="'#0ea5e9'"
                routerLink="/orders"
                routerLinkActive="selected"
                routerDirection="root"
                detail="false"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon
                    [icon]="menuIcons.orders"
                    aria-hidden="true"
                  ></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ 'menu.myOrders' | translate }}</h3>
                </ion-label>
              </ion-item>
            </ion-menu-toggle>
            <ion-menu-toggle autoHide="false">
              <ion-item
                class="nav-item"
                [style.--tone]="'#ec4899'"
                routerLink="/wishlist"
                routerLinkActive="selected"
                routerDirection="root"
                detail="false"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon
                    [icon]="menuIcons.wishlist"
                    aria-hidden="true"
                  ></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ 'menu.wishlist' | translate }}</h3>
                </ion-label>
              </ion-item>
            </ion-menu-toggle>
            <ion-menu-toggle autoHide="false">
              <ion-item
                class="nav-item"
                button="true"
                [style.--tone]="'#ef4444'"
                detail="false"
                (click)="logout()"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon
                    [icon]="menuIcons.logout"
                    aria-hidden="true"
                  ></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ 'menu.logout' | translate }}</h3>
                </ion-label>
              </ion-item>
            </ion-menu-toggle>
          } @else {
            <ion-menu-toggle autoHide="false">
              <ion-item
                class="nav-item"
                [style.--tone]="'#6366f1'"
                routerLink="/login"
                routerLinkActive="selected"
                routerDirection="root"
                detail="false"
              >
                <span slot="start" class="nav-ico">
                  <ion-icon
                    [icon]="menuIcons.login"
                    aria-hidden="true"
                  ></ion-icon>
                </span>
                <ion-label>
                  <h3>{{ 'menu.login' | translate }}</h3>
                  <p>{{ 'menu.loginDesc' | translate }}</p>
                </ion-label>
              </ion-item>
            </ion-menu-toggle>
          }
        </ion-list>
      </nav>
    </ion-content>

    <!-- Settings, pinned to the bottom of the drawer. -->
    <ion-footer class="ion-no-border">
      <ion-toolbar class="menu-foot">
        <div class="settings">
          <div
            class="theme-switch"
            role="radiogroup"
            [attr.aria-label]="'theme.title' | translate"
          >
            @for (m of themeModes; track m) {
              <button
                type="button"
                role="radio"
                [attr.aria-checked]="theme.mode() === m"
                [class.on]="theme.mode() === m"
                [attr.title]="'theme.' + m | translate"
                (click)="theme.setMode(m)"
              >
                <ion-icon [icon]="menuIcons[m]" aria-hidden="true"></ion-icon>
                <span>{{ 'theme.' + m | translate }}</span>
              </button>
            }
          </div>

          <!-- Language: a card that opens an inline list (the drawer's
               collapsible pattern). Each option shows its own name, its code,
               and its name in the current language, so a reader can find
               their way back from a language they can't read. -->
          <div class="settings-row">
            <button
              type="button"
              class="lang-toggle"
              [attr.aria-expanded]="langOpen()"
              aria-controls="menu-lang"
              (click)="langOpen.set(!langOpen())"
            >
              <span class="lang-ico" aria-hidden="true">
                <ion-icon [icon]="menuIcons.language"></ion-icon>
              </span>
              <span class="lang-text">
                <small>{{ 'menu.language' | translate }}</small>
                <strong [attr.lang]="currentLanguage().code">{{
                  currentLanguage().label
                }}</strong>
              </span>
              <img
                class="lang-flag"
                [src]="currentLanguage().flag"
                alt=""
                width="24"
                height="16"
              />
              <ion-icon
                class="lang-chevron"
                [icon]="menuIcons.chevron"
                aria-hidden="true"
              ></ion-icon>
            </button>

            <!-- Which build this is: dev and prod differ only in the backend
                 they call, which is otherwise invisible. The endpoint itself
                 is deliberately not shown. -->
            <span
              class="env"
              [attr.data-env]="environmentName"
              [attr.title]="'menu.environment' | translate"
            >
              <i aria-hidden="true"></i>{{ environmentName }}
            </span>
          </div>

          <div
            id="menu-lang"
            class="collapsible lang-panel"
            [class.open]="langOpen()"
            [inert]="!langOpen()"
          >
            <div
              class="lang-list"
              role="radiogroup"
              [attr.aria-label]="'menu.language' | translate"
            >
              @for (l of languages; track l.code) {
                <button
                  type="button"
                  role="radio"
                  class="lang-option"
                  [class.on]="l.code === currentLang()"
                  [attr.aria-checked]="l.code === currentLang()"
                  (click)="setLanguage(l.code)"
                >
                  <img
                    class="lang-flag"
                    [src]="l.flag"
                    alt=""
                    width="24"
                    height="16"
                  />
                  <span class="lang-text">
                    <strong [attr.lang]="l.code">{{ l.label }}</strong>
                    @if (languageHints()[l.code]; as hint) {
                      <small>{{ hint }}</small>
                    }
                  </span>
                  <span class="lang-code" aria-hidden="true">{{ l.code }}</span>
                  @if (l.code === currentLang()) {
                    <ion-icon
                      class="lang-check"
                      [icon]="menuIcons.check"
                      aria-hidden="true"
                    ></ion-icon>
                  }
                </button>
              }
            </div>
          </div>

          <ion-menu-toggle autoHide="false" class="menu-legal">
            <a routerLink="/privacy">{{ 'menu.privacy' | translate }}</a>
          </ion-menu-toggle>
        </div>
      </ion-toolbar>
    </ion-footer>
  </ion-menu>

  <ion-router-outlet id="main-content"></ion-router-outlet>

  <!-- Floating Admin button (admins only): fans out the admin pages. -->
  <app-admin-fab></app-admin-fab>
</ion-app>
